import { RotateCcw, Search } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ReportFilters as Filters } from '@/types/report.types';

interface ReportFiltersProps {
  filters: Filters;
  onApply: (filters: Filters) => void;
}

const ROLES = ['armed', 'unarmed', 'supervisor', 'patrol', 'event'] as const;
const SITES = ['FL', 'NY'] as const;

export function ReportFilters({ filters, onApply }: ReportFiltersProps) {
  const { t } = useTranslation();
  const [local, setLocal] = useState<Filters>(filters);

  const handleApply = () => {
    onApply(local);
  };

  const handleReset = () => {
    const empty: Filters = {};
    setLocal(empty);
    onApply(empty);
  };

  return (
    <div className='flex flex-wrap items-end gap-3 rounded-lg bg-white p-4 shadow'>
      {/* From date */}
      <div>
        <label className='block text-xs font-medium text-gray-500 mb-1'>{t('reports.filters.from')}</label>
        <input
          type='date'
          value={local.from ?? ''}
          onChange={(e) => setLocal({ ...local, from: e.target.value || undefined })}
          className='rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none'
        />
      </div>

      {/* To date */}
      <div>
        <label className='block text-xs font-medium text-gray-500 mb-1'>{t('reports.filters.to')}</label>
        <input
          type='date'
          value={local.to ?? ''}
          onChange={(e) => setLocal({ ...local, to: e.target.value || undefined })}
          className='rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none'
        />
      </div>

      {/* Roles */}
      <div>
        <label className='block text-xs font-medium text-gray-500 mb-1'>{t('reports.filters.roles')}</label>
        <select
          value={local.roles ?? ''}
          onChange={(e) => setLocal({ ...local, roles: e.target.value || undefined })}
          className='rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none'>
          <option value=''>{t('reports.filters.allRoles')}</option>
          {ROLES.map((role) => (
            <option key={role} value={role}>
              {role.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}
            </option>
          ))}
        </select>
      </div>

      {/* Sites */}
      <div>
        <label className='block text-xs font-medium text-gray-500 mb-1'>{t('reports.filters.sites')}</label>
        <select
          value={local.sites ?? ''}
          onChange={(e) => setLocal({ ...local, sites: e.target.value || undefined })}
          className='rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none'>
          <option value=''>{t('reports.filters.allSites')}</option>
          {SITES.map((site) => (
            <option key={site} value={site}>
              {site}
            </option>
          ))}
        </select>
      </div>

      {/* Buttons */}
      <button
        onClick={handleApply}
        className='inline-flex items-center gap-1.5 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700'>
        <Search className='h-4 w-4' />
        {t('reports.filters.apply')}
      </button>
      <button
        onClick={handleReset}
        className='inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100'>
        <RotateCcw className='h-4 w-4' />
        {t('reports.filters.reset')}
      </button>
    </div>
  );
}
